import { useCallback, useEffect, useRef, useState } from "react";

export interface Placement {
  top: number;
  left: number;
  transform: "translateX(-50%)" | "translate(-50%, -100%)";
}

interface UsePortalPlacementOptions {
  /** Recomputes placement on window scroll/resize while open. Default false. */
  trackWhileOpen?: boolean;
}

export function usePortalPlacement<T extends HTMLElement>(
  computePlacement: (anchor: T | null) => Placement,
  options: UsePortalPlacementOptions = {}
) {
  const { trackWhileOpen = false } = options;
  const anchorRef = useRef<T>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<Placement>({
    top: 0,
    left: 0,
    transform: "translateX(-50%)",
  });

  const reposition = useCallback(() => {
    setCoords(computePlacement(anchorRef.current));
  }, [computePlacement]);

  const open = useCallback(() => {
    reposition();
    setIsOpen(true);
  }, [reposition]);

  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen || !trackWhileOpen) return;
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [isOpen, reposition, trackWhileOpen]);

  return { anchorRef, isOpen, coords, open, close, reposition };
}
